import type { HttpService } from '@empathyco/playboard-core-domain'
import type {
  PlatformToolReportDailyServiceResponse,
  PlatformToolReportEntryEntity,
  PlatformToolReportServiceResponse,
  PlatformToolReportTotalsServiceResponse,
  ToolReportingAdapter,
} from '../adapter'
import type {
  PlatformToolReportingListParams,
  RegularFilterConfig,
  ToolReportDailyEntry,
  ToolReportEntry,
  ToolReportingDailyParams,
  ToolReportingListParams,
  ToolReportTotals,
} from '../models'
import type { ContextProvider } from '../providers'
import type { ToolReportingService } from './interfaces'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable } from 'inversify'
import { TYPES } from '../config/inversify.types'
import { FiltersUtils } from '../utils'

@injectable()
export class PlatformToolReportingService implements ToolReportingService {
  private readonly TOOLS_ENDPOINT: string
  private readonly TOOLS_PAGINATION_ENDPOINT: string
  private readonly DAILY_TOOLS_ENDPOINT: string

  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService,
    @inject(TYPES.StatsUrl) protected url: string,
    @inject(TYPES.ContextProvider) protected contextProvider: ContextProvider,
    @inject(TYPES.ToolReportingAdapter)
    protected adapter: ToolReportingAdapter<
      PlatformToolReportEntryEntity,
      PlatformToolReportingListParams
    >,
    protected basePath = '/{instanceId}/',
  ) {
    this.TOOLS_ENDPOINT = `${basePath}Tools`
    this.TOOLS_PAGINATION_ENDPOINT = `${basePath}ToolsPagination`
    this.DAILY_TOOLS_ENDPOINT = `${basePath}DailyTools`
  }

  async list(params: ToolReportingListParams): Promise<Array<ToolReportEntry>> {
    try {
      const response = await this.httpService.get<PlatformToolReportServiceResponse>(
        this.buildRequestUrl(this.TOOLS_ENDPOINT),
        {
          addAccessToken: true,
          parameters: this.buildParams(params),
        },
      )
      return this.decodeResponseData(response.data?.data)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async listTotals(params: ToolReportingListParams): Promise<ToolReportTotals> {
    try {
      const response = await this.httpService.get<PlatformToolReportTotalsServiceResponse>(
        this.buildRequestUrl(this.TOOLS_PAGINATION_ENDPOINT),
        {
          addAccessToken: true,
          parameters: this.buildParams(params),
        },
      )
      return this.adapter.decodeTotals(response.data?.data[0])
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async getDailyData(params: ToolReportingDailyParams): Promise<ToolReportDailyEntry[]> {
    try {
      const response = await this.httpService.get<PlatformToolReportDailyServiceResponse>(
        this.buildRequestUrl(this.DAILY_TOOLS_ENDPOINT),
        {
          addAccessToken: true,
          parameters: this.buildParams(params),
        },
      )
      const dailyData = response?.data?.data?.daily_data
      if (!dailyData) {
        return []
      }

      return dailyData.map(toolReportDaily => this.adapter.decodeDaily(toolReportDaily))
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private getFiltersByDefault(): Record<string, string> {
    const { filters } = this.contextProvider()
    const filterConfig = FiltersUtils.findConfig(
      Object.values(filters).map(filterObj => filterObj.config),
      {
        taggingId: 'scope',
      },
    ) as RegularFilterConfig
    const filterId = filterConfig.taggingId ?? filterConfig.id
    return {
      filters: filterId,
      [filterId]: filterConfig.values[0].id,
    }
  }

  private buildParams(params: ToolReportingListParams): PlatformToolReportingListParams {
    return { ...this.adapter.encodeParams(params), ...this.getFiltersByDefault() }
  }

  private buildRequestUrl(endpoint?: string, parameters: Record<string, any> = {}): string {
    const url = `${this.url}${endpoint}`
    const { instanceId } = this.contextProvider()
    return interpolate(url, { ...parameters, instanceId })
  }

  private decodeResponseData(
    responseData: Array<PlatformToolReportEntryEntity> | undefined,
  ): Array<ToolReportEntry> {
    return responseData ? responseData.map(toolReport => this.adapter.decode(toolReport)) : []
  }
}
